// Передать остаток другому подотчётному лицу. Сценарий 56.
//
// В вебе это оформление УЖЕ СЛУЧИВШЕЙСЯ передачи: старший закончил вахту и отдал деньги
// сменщику, финотдел заносит это в систему за обоих. Поэтому передача проводится сразу, без
// шага подтверждения, и сценарий сходится в одно действие.
//
// Двухшаговый путь («в пути», пока принимающий не подтвердил) нужен приложению: там передаёт
// человек в поле, а подтверждает сменщик со своего телефона.
//
// После успеха показываются ОБА остатка числами от сервера. Их сумма обязана совпасть с
// суммой до операции: это перекладывание, а не создание денег.

import type { FormEvent } from 'react';
import { useState } from 'react';

import type { ApiError } from '@/shared/errors/api-error';
import { newIdempotencyKey } from '@/shared/api/idempotency';
import { useAuthStore } from '@/shared/auth/store';
import { formatKopecks } from '@/shared/money/format';
import { moneyErrorText, parseRublesToKopecks } from '@/shared/money/parse';
import { can } from '@/shared/rbac/permissions';
import * as Alert from '@/shared/ui/alert';
import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import { TextField } from '@/shared/ui/text-field';

import { usePerson, usePersons, useStartTransfer } from '../queries';

const TRANSFER_PERMISSION = 'core.accountable.transfer';

export function TransferPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const права = useAuthStore((state) => state.permissions);
  const можно = can(права, TRANSFER_PERMISSION);
  const отправитель = usePerson(id);
  const все = usePersons({});
  const операция = useStartTransfer();

  const [кому, setКому] = useState(NO_VALUE);
  const [сумма, setСумма] = useState('');
  const [основание, setОснование] = useState('');
  const [ошибкаСуммы, setОшибкаСуммы] = useState<string | undefined>(undefined);
  const [запрет, setЗапрет] = useState<string | null>(null);
  const [ключ, setКлюч] = useState(newIdempotencyKey);

  const итог = операция.data;
  const баланс = отправитель.data?.balance;
  // Пока баланс не приехал, свободный остаток НЕИЗВЕСТЕН. Прежняя пара `?? 0` давала на всё
  // время запроса честное на вид «0,00», по которому человек решал, сколько можно передать
  // (правило 7 и правило 11 в ПРАВИЛА.md).
  const свободно = баланс === undefined ? null : баланс.unconfirmed_balance - баланс.in_transit_out;
  // Себя в списке получателей нет: передача самому себе смысла не имеет, и сервер её отвергнет.
  const получатели = (все.data?.items ?? []).filter((строка) => строка.id !== id);

  function отправить(событие: FormEvent<HTMLFormElement>) {
    событие.preventDefault();
    if (!можно) {
      setЗапрет('Оформлять передачу может финансовый отдел или сам подотчётный.');
      return;
    }
    if (операция.isPending) {
      setЗапрет('Передача уже проводится, ждём ответ сервера.');
      return;
    }
    if (кому === NO_VALUE) {
      setЗапрет('Выберите, кому передают деньги.');
      return;
    }
    const разбор = parseRublesToKopecks(сумма);
    if (!разбор.ok) {
      setОшибкаСуммы(moneyErrorText(разбор.reason, 'сумма передачи'));
      return;
    }
    setОшибкаСуммы(undefined);
    setЗапрет(null);
    операция.mutate(
      {
        body: {
          from_person_id: id,
          to_person_id: кому,
          amount: разбор.value,
          basis: основание.trim() === '' ? null : основание.trim(),
          // Финотдел заносит уже случившуюся передачу за обоих: подтверждать нечего.
          confirm_immediately: true,
        },
        idempotencyKey: ключ,
      },
      {
        onSuccess: () => {
          setКлюч(newIdempotencyKey());
          setСумма('');
        },
      },
    );
  }

  return (
    <form className='flex flex-col gap-4' onSubmit={отправить}>
      <p className='text-paragraph-sm text-text-sub-600'>
        От кого: {отправитель.data?.employee_name ?? 'загружаем…'} · свободный остаток{' '}
        {свободно === null ? 'загружаем…' : formatKopecks(свободно)}
        {баланс !== undefined && баланс.in_transit_out > 0 && (
          <> (ещё {formatKopecks(баланс.in_transit_out)} уже в пути)</>
        )}
      </p>

      <SelectField
        id='transfer-to'
        label='Кому передают'
        required
        value={кому}
        placeholder='выберите подотчётное лицо'
        pending={все.isPending}
        failed={все.isError}
        failedError={все.error as ApiError | null}
        emptyNote='Других подотчётных лиц нет: передавать некому. Заведите второе лицо в разделе «Суточные расходы».'
        options={получатели.map((строка) => ({
          value: строка.id,
          label: `${строка.employee_name} · ${formatKopecks(строка.balance.unconfirmed_balance)}`,
        }))}
        onChange={setКому}
      />

      <TextField
        id='transfer-amount'
        label='Сумма'
        required
        value={сумма}
        onChange={setСумма}
        // Образец записи, а не свободный остаток. Раньше здесь стояло `свободно / 100` с
        // округлением: сумма показывалась в чужом формате и на копейки мимо. Настоящий остаток
        // стоит строкой выше, с подписью.
        placeholder='30 000'
        error={ошибкаСуммы}
        hint='Больше свободного остатка передать нельзя: иначе сумма уйдёт в минус у одного и задвоится у другого.'
      />

      <TextField
        id='transfer-basis'
        label='Основание'
        value={основание}
        onChange={setОснование}
        placeholder='закончилась вахта, передал сменщику'
      />

      {запрет !== null && (
        <Alert.Root variant='lighter' status='warning' size='small'>
          <Alert.Icon />
          {запрет}
        </Alert.Root>
      )}
      {операция.error !== null && <ErrorAlert error={операция.error as never} />}

      {итог !== undefined && (
        <Alert.Root variant='lighter' status='success' size='small'>
          <Alert.Icon />
          Передано {formatKopecks(итог.amount)}.{' '}
          {итог.sender_balance_after === null || итог.receiver_balance_after === null ? (
            <>
              Новые остатки сервер не прислал — посмотрите их в карточках {итог.from_employee_name}{' '}
              и {итог.to_employee_name}.
            </>
          ) : (
            <>
              У {итог.from_employee_name} стало {formatKopecks(итог.sender_balance_after)}, у{' '}
              {итог.to_employee_name} — {formatKopecks(итог.receiver_balance_after)}. Сумма по обоим
              не изменилась: это перекладывание, а не создание денег.
            </>
          )}
        </Alert.Root>
      )}

      <div className='flex gap-2'>
        <Button.Root type='submit' variant='primary' mode='filled' size='small'>
          {операция.isPending ? 'Проводим…' : 'Передать'}
        </Button.Root>
        <Button.Root type='button' variant='neutral' mode='stroke' size='small' onClick={onClose}>
          {итог !== undefined ? 'Готово' : 'Отмена'}
        </Button.Root>
      </div>
    </form>
  );
}
